import * as React from 'react';
import {useEffect, useState} from 'react';
import {Navigate, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import Leads from "./dashboard-pages/Leads";
import Bookings from "./dashboard-pages/Bookings";
import GeneralApiService, {GetUser, LogoutUser, VerifyLoggedIn} from "../axios/Axios";
import Customers from "./dashboard-pages/Customers";
import LeadGeneration from "./dashboard-pages/LeadGeneration";
import Box from "@mui/joy/Box";
import {CircularProgress} from "@mui/joy";
import Calendar from "./dashboard-pages/calendar/Calendar";
import RecurringBookings from "./dashboard-pages/Recurring-Bookings";
import Messages from "./dashboard-pages/messages/Messages";
import Invoices from "./dashboard-pages/Invoices";
import Payouts from "./dashboard-pages/Payouts";
import TodaysBookings from "./dashboard-pages/TodaysBookings";
import FranchiseSettings from "./dashboard-pages/FranchiseSettings";
import FranchiseFees from "./dashboard-pages/FranchiseFees";
import KnowledgeBase from "./dashboard-pages/KnowledgeBase";
import KnowledgeBaseCourseDetail from "./dashboard-pages/KnowledgeBaseCourseDetail";
import Home from "./dashboard-pages/Home";
import CallLog from "./dashboard-pages/CallLog";
import Media from "./dashboard-pages/Media";
import Cookies from 'js-cookie';

import DashboardLayout from "./dashboard-components/DashboardLayout";
import MySales from "./dashboard-pages/MySales";
import SalesTeam from "./dashboard-pages/SalesTeam";
import SalesActions from "./dashboard-pages/SalesActions";
import {useUser} from "./dashboard-components/UserContext";
import userActivitySocketService from "../awebsocket/UserActivityWebSocketService";
import Vans from "./dashboard-pages/Vans";
import Team from "./dashboard-pages/Team";
import FranchiseAdminOverview from "./dashboard-pages/FranchiseAdminOverview";
import Voicemails from "./dashboard-pages/Voicemails";
import NeedsRescheduling from "./dashboard-pages/NeedsRescheduling";
import LeafletRequests from "./dashboard-pages/LeafletRequests";
import Expenses from "./dashboard-pages/Expenses";
import SupportTickets from "./dashboard-pages/support/SupportTickets";
import Ronald from "./dashboard-pages/ronald/Ronald";
import PotentialZees from "./dashboard-pages/PotentialZees";
import {hasOverdueUnpaidFranchiseFee} from "../lib/franchise-fee-lock";

interface User {
    location_permissions: string[];
    is_superuser?: boolean;
}

const PERMISSIONS = {
    SUPER_ADMIN: 'super_admin',
    ADMIN: 'admin',
    EMPLOYEE: 'employee',
    SALES: 'sales',
};

export default function DashboardRouter() {
    let navigate = useNavigate()
    const routerLocation = useLocation();
    const [authenticated, setAuthenticated] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const [locationSet, setLocationSet] = useState(false);
    const [userPermissions, setUserPermissions] = useState<string[]>([]);
    const [isDjangoSuperUser, setIsDjangoSuperUser] = useState(false);
    const [crmRestrictedToFees, setCrmRestrictedToFees] = useState(false);
    const userContext = useUser() as { user: unknown; isLoading: boolean } | null;
    const user = userContext?.user;
    const isLoading = userContext?.isLoading ?? true;

    useEffect(() => {
        if (!isLoading && user) {
            console.log("User authenticated, connecting to WebSocket...");
            userActivitySocketService.connect();
        }

        return () => {
            console.log("Cleaning up WebSocket connection.");
            userActivitySocketService.disconnect();
        };
    }, [user, isLoading]);



    // This entire useEffect hook remains unchanged.
    // Its logic for authentication, permissions, and location is perfectly fine.
    useEffect(() => {
        const checkUserStatus = async () => {

            try {
                let localStorageLocationSet = localStorage.getItem('location') !== null;
                const response = await VerifyLoggedIn();
                if (response.status === 200) {
                    setAuthenticated(true);
                    const userResponse = await GetUser();
                    if (userResponse.status !== 200) {
                        return <Navigate to="/"/>;
                    }
                    if (localStorageLocationSet) {
                        let franchise = localStorage.getItem('location')
                        const permissions = userResponse.data.location_permissions;
                        const isDjangoSuperUserResponse = Boolean(userResponse.data.is_superuser);
                        setUserPermissions(permissions);
                        setIsDjangoSuperUser(isDjangoSuperUserResponse);

                        // @ts-ignore
                        let permission = userResponse.data.location_permissions.includes('franchise.location.' + franchise?.toLowerCase().replaceAll(' ', '_'))
                        const hasAllLocationAccess = userResponse.data.location_permissions.includes('franchise.location.all');
                        if (hasAllLocationAccess) {
                            permission = true;
                        }
                        if (franchise === 'All' && (permissions.includes(PERMISSIONS.SALES) || permissions.includes('sales_admin'))) {
                            permission = true;
                        }
                        if (!permission) {
                            setLocationSet(false)
                        } else {
                            setLocationSet(true)

                            const shouldCheckFeeLock = permissions.includes(PERMISSIONS.ADMIN)
                                && !permissions.includes(PERMISSIONS.SUPER_ADMIN)
                                && !hasAllLocationAccess
                                && franchise !== 'All';

                            if (shouldCheckFeeLock) {
                                try {
                                    const feesData = await GeneralApiService.getFranchiseFees();
                                    setCrmRestrictedToFees(Array.isArray(feesData) && hasOverdueUnpaidFranchiseFee(feesData));
                                } catch (err) {
                                    console.error("Failed to check franchise fee CRM restriction:", err);
                                    setCrmRestrictedToFees(false);
                                }
                            } else {
                                setCrmRestrictedToFees(false);
                            }
                        }
                    }
                }
            } catch (err) {
                Cookies.remove('yeti-auth')
                Cookies.remove('yeti-refresh-token')
                await LogoutUser();
                navigate("/");
            } finally {
                setInitializing(false);
            }
        };

        checkUserStatus();
    }, []);


    // These initial checks remain unchanged.
    if (initializing) {
        return <Box sx={{position: 'absolute', left: '50%', top: '50%'}}>
            <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
                height: '100%',
                width: '100%',
                justifyContent: 'center'
            }}>
                <CircularProgress
                    size="lg"
                />
            </Box>
        </Box>;
    }
    if (!locationSet) {
        localStorage.removeItem('location')
        return <Navigate to="/select-location"/>;
    }
    if (!authenticated) {
        return <Navigate to="/"/>;
    }
    if (crmRestrictedToFees && !routerLocation.pathname.startsWith('/dashboard/franchise-fees')) {
        return <Navigate to="/dashboard/franchise-fees" replace/>;
    }

    // This function generates routes based on permissions and selected location context.
    const renderRoutes = () => {
        const isSuperAdmin = userPermissions.includes(PERMISSIONS.SUPER_ADMIN);
        const selectedLocation = localStorage.getItem('location');
        const hasAdminPermission = userPermissions.includes(PERMISSIONS.ADMIN);
        const hasSalesPermission = userPermissions.includes(PERMISSIONS.SALES) || userPermissions.includes('sales_admin');
        const isSalesMode = selectedLocation === 'All' && hasSalesPermission && !isSuperAdmin;

        if (isSalesMode) {
            return (
                <>
                    <Route path="/" element={<Leads/>}/>
                    <Route path="/leads" element={<Leads/>}/>
                    <Route path="/customers" element={<Customers/>}/>
                    <Route path="/messages" element={<Messages/>}/>
                    <Route path="/calls" element={<CallLog/>}/>
                    <Route path="/bookings" element={<Bookings/>}/>
                    <Route path="/recurring-bookings" element={<RecurringBookings/>}/>
                    <Route path="/calendar" element={<Calendar/>}/>
                    <Route path="/sales-actions" element={<Navigate to="/dashboard/callback-reminders" replace/>}/>
                    <Route path="/callback-reminders" element={<SalesActions/>}/>
                    <Route path="/todays-bookings" element={<TodaysBookings/>}/>
                    <Route path="/knowledge-base" element={<KnowledgeBase/>}/>
                    <Route path="/knowledge-base/:slug" element={<KnowledgeBaseCourseDetail/>}/>
                    <Route path="/mysales" element={<MySales/>}/>
                    <Route path="/voicemails" element={<Voicemails/>}/>
                </>
            );
        } else if (hasAdminPermission) {
            return (
                <>
                    {userPermissions.includes(PERMISSIONS.SUPER_ADMIN) && (
                        <Route path="/franchise-admin" element={<FranchiseAdminOverview/>}/>
                    )}
                    {isDjangoSuperUser && (
                        <Route path="/potential-zees" element={<PotentialZees/>}/>
                    )}
                    <Route path="/" element={<Home/>}/>
                    <Route path="/leads" element={<Leads/>}/>
                    <Route path="/customers" element={<Customers/>}/>
                    <Route path="/calls" element={<CallLog/>}/>
                    <Route path="/voicemails" element={<Voicemails/>}/>
                    <Route path="/bookings" element={<Bookings/>}/>
                    <Route path="/recurring-bookings" element={<RecurringBookings/>}/>
                    <Route path="/media" element={<Media/>}/>
                    <Route path="/lead-generation" element={<LeadGeneration/>}/>
                    <Route path="/knowledge-base" element={<KnowledgeBase/>}/>
                    <Route path="/knowledge-base/:slug" element={<KnowledgeBaseCourseDetail/>}/>
                    <Route path="/calendar" element={<Calendar/>}/>
                    <Route path="/sales-actions" element={<Navigate to="/dashboard/callback-reminders" replace/>}/>
                    <Route path="/callback-reminders" element={<SalesActions/>}/>
                    <Route path="/messages" element={<Messages/>}/>
                    <Route path="/todays-bookings" element={<TodaysBookings/>}/>
                    <Route path="/invoices" element={<Invoices/>}/>
                    <Route path="/payouts" element={<Payouts/>}/>
                    <Route path="/vans" element={<Vans/>}/>
                    <Route path="/team" element={<Team/>}/>
                    <Route path="/franchise-settings" element={<FranchiseSettings/>}/>
                    <Route path="/expenses" element={<Expenses/>}/>
                    <Route path="/franchise-fees" element={<FranchiseFees/>}/>
                    <Route path="/needs-rescheduling" element={<NeedsRescheduling/>}/>
                    <Route path="/leaflet-requests" element={<LeafletRequests/>}/>
                    <Route path="/sales-team" element={<SalesTeam/>}/>
                    <Route path="/support-tickets" element={<SupportTickets/>}/>
                    {hasSalesPermission && (
                        <Route path="/mysales" element={<MySales/>}/>
                    )}
                    {isSuperAdmin && (
                        <Route path="/ronald" element={<Ronald/>}/>
                    )}

                </>
            );
        } else if (userPermissions.includes(PERMISSIONS.SALES)) {
            return (
                <>
                    <Route path="/" element={<Leads/>}/>
                    <Route path="/leads" element={<Leads/>}/>
                    <Route path="/customers" element={<Customers/>}/>
                    <Route path="/messages" element={<Messages/>}/>
                    <Route path="/calls" element={<CallLog/>}/>
                    <Route path="/bookings" element={<Bookings/>}/>
                    <Route path="/recurring-bookings" element={<RecurringBookings/>}/>
                    <Route path="/calendar" element={<Calendar/>}/>
                    <Route path="/sales-actions" element={<Navigate to="/dashboard/callback-reminders" replace/>}/>
                    <Route path="/callback-reminders" element={<SalesActions/>}/>
                    <Route path="/todays-bookings" element={<TodaysBookings/>}/>
                    <Route path="/knowledge-base" element={<KnowledgeBase/>}/>
                    <Route path="/knowledge-base/:slug" element={<KnowledgeBaseCourseDetail/>}/>
                    <Route path="/mysales" element={<MySales/>}/>
                    <Route path="/voicemails" element={<Voicemails/>}/>
                </>
            );
        } else if (isSuperAdmin || isDjangoSuperUser) {
            // Pure Yeti HQ super admin / Django superuser (no franchise-level admin or sales role).
            return (
                <>
                    <Route path="/" element={<Navigate to={isDjangoSuperUser ? "/dashboard/potential-zees" : "/dashboard/ronald"} replace/>}/>
                    {isSuperAdmin && <Route path="/ronald" element={<Ronald/>}/>}
                    {isDjangoSuperUser && <Route path="/potential-zees" element={<PotentialZees/>}/>}
                    <Route path="/support-tickets" element={<SupportTickets/>}/>
                </>
            );
        } else if (userPermissions.includes(PERMISSIONS.EMPLOYEE)) {
            return (
                <>
                    <Route path="/" element={<TodaysBookings/>}/>
                    <Route path="/todays-bookings" element={<TodaysBookings/>}/>
                    <Route path="/calendar" element={<Calendar/>}/>
                    <Route path="/sales-actions" element={<Navigate to="/dashboard/callback-reminders" replace/>}/>
                    <Route path="/callback-reminders" element={<SalesActions/>}/>
                    <Route path="/knowledge-base" element={<KnowledgeBase/>}/>
                    <Route path="/knowledge-base/:slug" element={<KnowledgeBaseCourseDetail/>}/>
                </>
            );
        }
        }


    return (
        <>
            <Routes>
                <Route element={<DashboardLayout />}>
                    {renderRoutes()}
                </Route>
            </Routes>
        </>

    );
}